<script lang="ts">
	import TopMarketsTable from 'components/TopMarketsTable/TopMarketsTable.svelte';
	import ExchangeLinks from 'components/ExchangeLinks/ExchangeLinks.svelte';
	import type { IExchangeData } from 'customTypes/exchange';
	import theme from 'stores/theme';
	import { _ } from 'utils/i18/i18n';
	import formatNumber from 'utils/formatNumber';
	import activeCurrency from 'stores/activeCurrency';
	import FinancialReservesTable from 'components/FinancialReservesTable/FinancialReservesTable.svelte';
	import ExchangeStats from 'components/ExchangeStats/ExchangeStats.svelte';
	import ExchangeDescription from 'components/ExchangeDescription/ExchangeDescription.svelte';
	import MetaTags from 'components/MetaTags/MetaTags.svelte';
	import ExchangeHeaderSection from 'components/ExchangeHeaderSection/ExchangeHeaderSection.svelte';
	import Socials from 'components/SocialNetworks/Socials.svelte';
	import IntersectionObserver from 'components/IntersectionObserver/IntersectionObserver.svelte';
	import { CANONICAL_URL } from 'utils/urls';
	import SchemaOrgExchangesPage from 'src/components/SchemaOrg/SchemaOrgExchangesPage.svelte';

	$: dark = $theme as boolean;
	export let data: IExchangeData;
	const btcPrice = data.btcPrice;
	const metaCanonicalURL = `${CANONICAL_URL}/exchange/${data.route}`;
	const metaTitle =
		data?.customTitle ?? `${data?.customName ?? data?.name} Latest Trade Volume and Market Listings`;
	const metaDescription =
		data?.customMetadata ??
		`${
			data?.customName ?? data?.name
		} is a cryptocurrency exchange where you can buy & sell crypto coins. Find the latest trading volume & market listings of ${
			data?.customName ?? data?.name
		}.`;
	const stats = prepareStatistics(data);

	function prepareStatistics(raw: IExchangeData) {
		return {
			volume: btcPrice
				? formatNumber(
						(raw.trade_volume_1d_btc / btcPrice) * $activeCurrency.price,
						$activeCurrency.currency,
						false,
				  )
				: 0,
			rank: raw.trust_score_rank,
			listedCoins: formatNumber(raw.totalCoins, $activeCurrency.currency, false, 'decimal'),
			markets: formatNumber(raw.totalMarkets, $activeCurrency.currency, false, 'decimal'),
			share: `${Number(raw.market_share).toFixed(2)}%`,
			year: raw.year_established || '—',
		};
	}

	$: sortedReserves = data?.reserves?.sort(
		(a, b) => b.balance * b.currency.price_usd - a.balance * a.currency.price_usd,
	);
	$: totalReservesValue = data?.reserves?.reduce((acc, i) => {
		return (acc += i.balance * i.currency.price_usd);
	}, 0);
	$: lastUpdateTime = data?.reserves
		? new Date(data?.reserves[0]?.updatedAt).toLocaleTimeString('en-US', {
				hour: 'numeric',
				minute: '2-digit',
				second: '2-digit',
				hour12: true,
		  })
		: '--';
</script>

<SchemaOrgExchangesPage
	name={data.customName ?? data.name}
	route={data.route}
	description={metaDescription}
	{stats}
/>
<MetaTags
	title={metaTitle}
	description={metaDescription}
	canonical={metaCanonicalURL}
	additionalMetaTags={[
		{ property: 'og:type', content: 'website' },
		{ property: 'og:title', content: metaTitle },
		{ property: 'og:description', content: metaDescription },
		{ property: 'og:image', content: data.image },
		{ property: 'og:url', content: metaCanonicalURL },
		{ property: 'og:site_name', content: 'CryptoMarketCap' },
	]}
	twitter={data.social_links.twitter_handle}
/>
<div class="wrapper" class:dark>
	<div class="section-wrapper overall-info">
		<ExchangeHeaderSection {dark} {data} />
	</div>
	<div class="section-wrapper stats-wrapper">
		<section class="statistics">
			<h2 class="section-title" id="statistics">{$_('exchange.statistics')}</h2>
			<ExchangeStats {dark} {stats} />
		</section>
		<section class="description">
			<ExchangeDescription {dark} {data} {btcPrice} copywrite={null} />
		</section>
	</div>
	<div class="section-wrapper link-markets-wrapper">
		<section class="links">
			<h2 class="section-title">{$_('exchange.exchangeLinks')}</h2>
			<ExchangeLinks {dark} data={data?.social_links} backupLink={data?.url} ref={data?.ref} />
		</section>
		<section class="top-markets" class:dark>
			<div class="text-wrapper">
				<h2 class="section-title">{$_('exchange.topMarkets')}</h2>
				<p class="section-sub-text">
					{`${$_('exchange.topMarketsText')} ${data?.name} ${$_('exchange.topMarketsText2')}`}
				</p>
			</div>
			<TopMarketsTable
				{dark}
				tableData={data?.topMarkets}
				coingeckoId={data?.id}
				exchangeType={data?.type}
			/>
		</section>
	</div>
	<section class="social-networks" class:dark>
		<Socials
			redditUrl={data.social_links.reddit_url}
			twitterUrl={data.social_links.twitter_handle}
			{dark}
			id={data.id}
		/>
	</section>
	{#if sortedReserves?.length > 0}
		<div class="section-wrapper reserves-wrapper">
			<section class="reserves" class:dark>
				<div class="text-wrapper">
					<h2 class="section-title">{$_('exchange.financialReserves')}</h2>
					<p class="section-sub-text">
						{$_('exchange.total')}
						{formatNumber(totalReservesValue * $activeCurrency.price, $activeCurrency.currency)}
					</p>
				</div>
				<FinancialReservesTable {dark} id={data.id} tableData={sortedReserves} />
			</section>
			<section class="allocation">
				<div class="text-wrapper no-padding">
					<h2 class="section-title">{$_('exchange.tokenAllocation')}</h2>
					<p class="section-sub-text">{$_('exchange.lastUpdate')} {`${lastUpdateTime}`}</p>
				</div>
				<IntersectionObserver let:intersecting once={true} top={200}>
					{#if intersecting}
						{#await import('components/TokenAllocation/TokenAllocation.svelte') then c}
							<svelte:component this={c.default} {dark} {totalReservesValue} {sortedReserves} />
						{/await}
					{/if}
				</IntersectionObserver>
			</section>
		</div>
	{/if}
</div>

<style lang="scss">
	@import 'exchange';
</style>
